import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiArrowRight, FiBriefcase, FiCheck, FiMapPin, FiX } from 'react-icons/fi';
import ProCheckoutButton from '../components/ProCheckoutButton';
import { useLanguage } from '../contexts/LanguageContext';
import { PRO_PRICE_LABEL } from '../upgradeExperience';
import './job-matches-upgrade.css';

const COPY = {
  en: {
    title: 'Get job matches tailored to you',
    returningTitle: 'Resume your job matches',
    description: 'Let Scout find and rank jobs around your CV and career goals.',
    returningDescription: 'Your trial has ended. Continue your personalized job search with Pro.',
    preferences: 'Your saved search',
    benefits: [
      ['Jobs matched to your CV', 'Scout searches for roles that fit your experience and preferences.'],
      ['See why each role fits', 'Review match scores, relevant strengths and potential gaps.'],
      ['Get ready to apply', 'Tailor your CV and find relevant people to contact.'],
    ],
    priceSuffix: '/ month',
    included: 'Job Matches and all Pro features',
    upgrade: 'Unlock Job Matches',
    resume: 'Resume Job Matches',
    continueFree: 'Keep searching for free',
    close: 'Close Job Matches upgrade',
  },
  ar: {
    title: 'احصل على وظائف تناسبك',
    returningTitle: 'استأنف مطابقة الوظائف',
    description: 'دع مساعد البحث يجد الوظائف ويرتبها بناءً على سيرتك الذاتية وأهدافك المهنية.',
    returningDescription: 'انتهت تجربتك المجانية. واصل البحث المخصص عن الوظائف مع Pro.',
    preferences: 'تفضيلات بحثك المحفوظة',
    benefits: [
      ['وظائف تناسب سيرتك الذاتية', 'يبحث مساعدك عن وظائف تتناسب مع خبراتك وتفضيلاتك.'],
      ['اعرف سبب ملاءمة كل وظيفة', 'راجع درجات المطابقة ونقاط القوة والجوانب التي تحتاج إلى تطوير.'],
      ['استعد للتقديم', 'خصص سيرتك الذاتية واعثر على الأشخاص المناسبين للتواصل معهم.'],
    ],
    priceSuffix: 'شهرياً',
    included: 'مطابقة الوظائف وجميع مزايا Pro',
    upgrade: 'فعّل مطابقة الوظائف',
    resume: 'استأنف مطابقة الوظائف',
    continueFree: 'واصل البحث مجاناً',
    close: 'إغلاق الترقية لمطابقة الوظائف',
  },
};

function labels(value) {
  const items = Array.isArray(value) ? value : typeof value === 'string' ? value.split(';') : [];
  return [...new Set(items.filter((item) => typeof item === 'string').map((item) => item.trim()).filter(Boolean))];
}

// Use preferences already loaded with the page; opening this popup does not run a search.
export function savedMatchPreferences(profile) {
  const scout = profile?.jobScouts?.[profile?.primaryScoutId];
  const answers = scout?.onboardingAnswers || profile?.onboardingAnswers;
  const locations = scout?.locationPreferences || profile?.locationPreferences;
  const roles = labels(scout?.goals?.targetTitles).length ? labels(scout.goals.targetTitles)
    : labels(answers?.targetDirection || profile?.targetRole);
  const cities = Array.isArray(locations?.targetLocations) ? locations.targetLocations
    .map((location) => labels([location?.city, location?.state, location?.country]).join(', ')).filter(Boolean) : [];
  const countries = labels(locations?.targetCountries || locations?.countries || answers?.targetCountries);
  return { roles: roles.slice(0, 2), locations: [...new Set(cities.length ? cities : countries)].slice(0, 2) };
}

export default function JobMatchesUpgrade({ user, profile, access, onClose }) {
  const { language } = useLanguage();
  const ar = language === 'ar';
  const copy = COPY[ar ? 'ar' : 'en'];
  const titleId = useId();
  const descriptionId = useId();
  const dialog = useRef(null);
  const expired = Number(access.endsAt) > 0 && Number(access.endsAt) <= Date.now();
  const preferences = savedMatchPreferences(profile);

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    element.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return createPortal(<dialog ref={dialog} className="opp-dialog opp-matches-upgrade" dir={ar ? 'rtl' : 'ltr'}
    aria-labelledby={titleId} aria-describedby={descriptionId}
    onCancel={(event) => { event.preventDefault(); onClose(); }}
    onClick={(event) => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
    }}>
    <button type="button" className="opp-icon-button opp-matches-upgrade-close" onClick={onClose} aria-label={copy.close}><FiX aria-hidden="true" /></button>
    <h2 id={titleId}>{expired ? copy.returningTitle : copy.title}</h2>
    <p id={descriptionId} className="opp-matches-upgrade-intro">{expired ? copy.returningDescription : copy.description}</p>
    {(preferences.roles.length > 0 || preferences.locations.length > 0) && <section className="opp-matches-upgrade-context" aria-label={copy.preferences}>
      <h3>{copy.preferences}</h3>
      <div>{preferences.roles.map((role) => <span key={role}><FiBriefcase aria-hidden="true" />{role}</span>)}
        {preferences.locations.map((location) => <span key={location}><FiMapPin aria-hidden="true" />{location}</span>)}</div>
    </section>}
    <ul className="opp-matches-upgrade-benefits">{copy.benefits.map(([title, body]) => <li key={title}>
      <FiCheck aria-hidden="true" /><div><strong>{title}</strong><p>{body}</p></div>
    </li>)}</ul>
    <div className="opp-matches-upgrade-price"><p><strong>{ar ? '20 دولاراً' : PRO_PRICE_LABEL}</strong> <span>{copy.priceSuffix}</span></p><p>{copy.included}</p></div>
    <div className="opp-matches-upgrade-actions">
      <ProCheckoutButton user={user} className="opp-button">{expired ? copy.resume : copy.upgrade}<FiArrowRight className="js-icon--directional" aria-hidden="true" /></ProCheckoutButton>
      <button type="button" className="opp-matches-upgrade-dismiss" onClick={onClose}>{copy.continueFree}</button>
    </div>
  </dialog>, document.body);
}
